import { Alert, Box, Button, LinearProgress } from "@mui/material";
import moment, { type Moment } from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useOperationsTimeline } from "../hooks/useOperationsTimeline";
import {
	MINUTE,
	NAVIGATORS,
	type DetailLevel,
	type NavigatorKind,
	type Selection,
	type TimeRange,
	type TimelineData,
	type TimelineView,
} from "../models";
import { findOccurrence, navigationEvents, rangeError } from "../timeline";
import { TimelineDetails } from "./TimelineDetails";
import { TimelineSummary } from "./TimelineSummary";
import { TimelineToolbar, type NavigatorState } from "./TimelineToolbar";
import {
	TimelineViewport,
	type TimelineViewportHandle,
} from "./TimelineViewport";
import "../operations-timeline.css";

const EMPTY: TimelineData = {
	billets: [],
	campaigns: [],
	workOrders: [],
	stops: [],
	rolls: [],
};
function defaultRange(): TimeRange {
	const end = Math.ceil(Date.now() / MINUTE) * MINUTE;
	return { start: end - 24 * 60 * MINUTE, end };
}
function initialRange(params: URLSearchParams): TimeRange {
	const fromUrl = {
		start: Date.parse(params.get("start") ?? ""),
		end: Date.parse(params.get("end") ?? ""),
	};
	return rangeError(fromUrl) ? defaultRange() : fromUrl;
}

export function OperationsTimeline() {
	const [params, setParams] = useSearchParams();
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const [range, setRange] = useState(() => initialRange(params));
	// Explicit tz avoids the plant-wide moment default configured by the app.
	const [start, setStart] = useState<Moment | null>(() =>
		moment.tz(range.start, timeZone),
	);
	const [end, setEnd] = useState<Moment | null>(() =>
		moment.tz(range.end, timeZone),
	);
	// The last 24 hours is the opening range; a picked range is what RESET undoes.
	const [rangeIsDefault, setRangeIsDefault] = useState(
		() => !params.get("start"),
	);
	const resetRange = () => {
		const next = defaultRange();
		setStart(moment.tz(next.start, timeZone));
		setEnd(moment.tz(next.end, timeZone));
		setRangeIsDefault(true);
	};
	const [view, setView] = useState<TimelineView>(
		params.get("view") === "stands" ? "stands" : "parallel",
	);
	const [detail, setDetail] = useState<DetailLevel>("individual");
	const [selection, setSelection] = useState<Selection | null>(null);
	const [active, setActive] = useState<{
		kind: NavigatorKind;
		id: number;
	} | null>(null);
	const [position, setPosition] = useState(range.start);
	const [pendingTime, setPendingTime] = useState<number | null>(null);
	const viewport = useRef<TimelineViewportHandle>(null);
	const query = useOperationsTimeline(range);
	const data = query.data ?? EMPTY;
	const invalid = rangeError({
		start: start?.valueOf() ?? NaN,
		end: end?.valueOf() ?? NaN,
	});
	const events = useMemo(() => navigationEvents(data, range), [data, range]);
	const navigators = Object.fromEntries(
		NAVIGATORS.map(({ kind }) => {
			const activeIndex =
				active?.kind === kind
					? events[kind].findIndex((event) => event.id === active.id)
					: -1;
			const current = activeIndex >= 0 ? activeIndex : null;
			return [
				kind,
				{
					total: events[kind].length,
					current,
					previous:
						findOccurrence(events[kind], position, -1, current) !== null,
					next: findOccurrence(events[kind], position, 1, current) !== null,
				},
			];
		}),
	) as Record<NavigatorKind, NavigatorState>;

	// The range applies on its own once the pickers hold a valid one. The pause lets a
	// date be typed digit by digit without a fetch per keystroke.
	useEffect(() => {
		if (invalid || !start || !end) return;
		const next = { start: start.valueOf(), end: end.valueOf() };
		if (next.start === range.start && next.end === range.end) return;
		const timer = setTimeout(() => {
			setSelection(null);
			setActive(null);
			setPosition(next.start);
			setPendingTime(null);
			setRange(next);
			setParams(
				(previous) => {
					const p = new URLSearchParams(previous);
					p.set("start", new Date(next.start).toISOString());
					p.set("end", new Date(next.end).toISOString());
					return p;
				},
				{ replace: true },
			);
		}, 600);
		return () => clearTimeout(timer);
	}, [invalid, start, end, range, setParams]);
	const navigate = (kind: NavigatorKind, direction: -1 | 1) => {
		const index = findOccurrence(
			events[kind],
			position,
			direction,
			navigators[kind].current,
		);
		if (index === null) return;
		const event = events[kind][index];
		setActive({ kind, id: event.id });
		setSelection({ kind: event.kind, id: event.id });
		setDetail("individual");
		setPendingTime(event.time);
	};
	useEffect(() => {
		if (pendingTime !== null && detail === "individual" && viewport.current) {
			viewport.current.scrollToTime(pendingTime);
			setPendingTime(null);
		}
	}, [pendingTime, detail, data]);
	const onPosition = useCallback((time: number, manual: boolean) => {
		setPosition(time);
		if (manual) setActive(null);
	}, []);
	const select = useCallback((value: Selection) => {
		setSelection(value);
		setActive(null);
	}, []);
	const onView = (value: TimelineView) => {
		setView(value);
		setParams(
			(previous) => {
				const p = new URLSearchParams(previous);
				p.set("view", value);
				return p;
			},
			{ replace: true },
		);
	};
	const hasEvents =
		data.billets.length +
			data.campaigns.length +
			data.workOrders.length +
			data.stops.length +
			data.rolls.length >
		0;
	return (
		<Box className="operations-timeline">
			<Box className="timeline-controls">
				<TimelineToolbar
					start={start}
					end={end}
					onStart={(value) => {
						setStart(value);
						setRangeIsDefault(false);
					}}
					onEnd={(value) => {
						setEnd(value);
						setRangeIsDefault(false);
					}}
					onReset={resetRange}
					canReset={!rangeIsDefault}
					busy={query.isFetching}
					view={view}
					onView={onView}
					detail={detail}
					onDetail={setDetail}
					navigators={navigators}
					navigate={navigate}
					timeZone={timeZone}
				/>
				<TimelineDetails
					data={data}
					selection={selection}
					clear={() => {
						setSelection(null);
						setActive(null);
					}}
				/>
			</Box>
			{invalid && <Alert severity="warning">{invalid}</Alert>}
			{query.isFetching && <LinearProgress aria-label="Loading timeline" />}
			{query.isError && (
				<Alert
					severity="error"
					action={
						<Button color="inherit" onClick={() => void query.refetch()}>
							Retry
						</Button>
					}
				>
					{(query.error as Error).message}
				</Alert>
			)}
			{!query.isLoading && !query.isError && !hasEvents && (
				<Alert severity="info">No recorded events in this range.</Alert>
			)}
			{query.data &&
				(detail === "summary" ? (
					<TimelineSummary
						data={data}
						range={range}
						open={(time) => {
							setDetail("individual");
							setPendingTime(time);
							setActive(null);
						}}
					/>
				) : (
					<TimelineViewport
						key={`${range.start}-${range.end}`}
						ref={viewport}
						data={data}
						range={range}
						view={view}
						selection={selection}
						onSelect={select}
						onPosition={onPosition}
						initialTime={pendingTime ?? position}
					/>
				))}
		</Box>
	);
}
